import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, CheckCircle, Clock, XCircle, Shield, Upload, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const VerificationPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selfiePreview, setSelfiePreview] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.getVerificationStatus();
      setStatus(response.data);
    } catch (error) {
      toast.error("Failed to load verification status");
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setSelfiePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!selfiePreview) {
      toast.error("Please take or upload a selfie");
      return;
    }

    setUploading(true);
    try {
      const response = await api.submitVerification(selfiePreview);
      const result = response.data;
      
      if (result.status === "approved") {
        toast.success("You're verified! AI matched your photos.");
        await refreshUser();
      } else if (result.status === "rejected") {
        toast.error(result.message || "Photos didn't match. Please try again.");
      } else {
        toast.success("Submitted for review!");
      }
      
      setSelfiePreview(null);
      await fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit verification");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your verification request?")) return;
    
    try {
      await api.cancelVerification();
      toast.success("Verification request cancelled");
      await fetchStatus();
    } catch (error) {
      toast.error("Failed to cancel request");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FAB710] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="verification-page">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/settings")} className="p-1 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-6 h-6 text-[#1C3A7A]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#1C3A7A]">Profile Verification</h1>
            <p className="text-sm text-gray-500">Get the verified badge</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-6">
        {/* Already Verified */}
        {status?.is_verified && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-800 mb-2">You're Verified!</h2>
            <p className="text-green-700">
              Your profile has a verified badge that shows others you're a real person.
            </p>
          </div>
        )}

        {/* Pending Request */}
        {status?.status === "pending" && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-bold text-amber-800 mb-1">Verification Pending</h2>
                <p className="text-amber-700 text-sm mb-4">
                  Your verification request is being reviewed. This usually takes 24-48 hours.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancel}
                  className="text-amber-700 border-amber-300"
                >
                  Cancel Request
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Rejected */}
        {status?.status === "rejected" && status?.last_rejection && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-red-800 mb-1">Verification Declined</h2>
                <p className="text-red-700 text-sm">
                  {status.last_rejection.rejection_reason || "Your previous request was not approved. Please try again with a clearer photo."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Submit New Request */}
        {!status?.is_verified && status?.status !== "pending" && (
          <>
            {/* Info Card */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#1C3A7A]/10 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#1C3A7A]" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-900">Why Get Verified?</h2>
                  <p className="text-sm text-gray-500">Stand out with a badge</p>
                </div>
              </div>
              
              <ul className="space-y-3 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Get a verified badge on your profile</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Show matches you're a real person</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Appear more trustworthy to others</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Get more matches!</span>
                </li>
              </ul>
            </div>

            {/* Selfie Upload */}
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Take a Verification Selfie</h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Take a clear photo of your face. We'll compare it to your profile photos to confirm your identity.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="user"
                onChange={handleFileSelect}
                className="hidden"
              />

              {selfiePreview ? (
                <div className="relative mb-4">
                  <img
                    src={selfiePreview}
                    alt="Selfie preview"
                    className="w-full h-64 object-cover rounded-xl"
                  />
                  <button
                    onClick={() => setSelfiePreview(null)}
                    className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-48 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-[#FAB710] hover:bg-[#FAB710]/5 transition-colors mb-4"
                  data-testid="selfie-upload"
                >
                  <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center">
                    <Camera className="w-7 h-7 text-gray-400" />
                  </div>
                  <div className="text-center">
                    <p className="font-medium text-gray-700">Take or upload a selfie</p>
                    <p className="text-sm text-gray-500">JPG, PNG up to 5MB</p>
                  </div>
                </button>
              )}

              {/* Tips */}
              <div className="bg-blue-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-blue-900 mb-2">Tips for a good selfie:</p>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Good lighting, face clearly visible</li>
                  <li>• No sunglasses or hats</li>
                  <li>• Look directly at the camera</li>
                  <li>• No filters or heavy editing</li>
                </ul>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={!selfiePreview || uploading}
                className="w-full bg-[#FAB710] hover:bg-[#e5a60f] text-white font-bold py-3"
                data-testid="submit-verification"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit for Verification
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerificationPage;
